import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useId, useRef } from "react";

import {
  AnalyzeAttemptDto,
  AnalyzeProgressDtoPhaseEnum,
} from "../lib/generated/api-client";
import {
  analyseElapsedLabel,
  analysePhaseLabel,
} from "../lib/analyse-phase-label";
import { Button } from "./ui/Button";

interface AnalyseProgressModalProps {
  attempt: AnalyzeAttemptDto | null;
  elapsedSeconds: number;
  datasourceName?: string | null;
  error?: string | null;
  onClose?: () => void;
}

const TIPS = [
  "OnlyDash reads up to 1000 rows from each table to find the type of each column.",
  "OnlyDash does not analyze views and materialized views.",
  "You can run the analysis again at any time.",
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const AnalyseProgressModal: React.FC<AnalyseProgressModalProps> = ({
  attempt,
  elapsedSeconds,
  datasourceName,
  error,
  onClose,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [reduceMotion, setReduceMotion] = React.useState(prefersReducedMotion);
  // One tip per dialog open. The old strip rotated every 4.5 s with a fade and
  // could not be paused, which fails WCAG 2.2.2. A fixed tip never changes, so
  // nobody has to chase it. Keyed on the attempt, so a retry gets a new tip.
  const [tip] = React.useState(() =>
    Math.floor(Math.random() * TIPS.length)
  );

  // Follow a mid-session change of the OS setting.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(query.matches);
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  // Same focus contract as components/Modal.tsx: focus moves in on open, Tab
  // and Shift+Tab wrap inside the panel, and focus goes back to the trigger on
  // close. This dialog does not auto-dismiss on success, so Escape only closes
  // it in the error state, where there is something useful to dismiss.
  const errorRef = useRef(!!error);
  errorRef.current = !!error;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      const panel = panelRef.current;
      if (!panel) return;
      // Only the dialog the user is in reacts, so a stacked Modal keeps its
      // own Escape.
      if (!panel.contains(event.target as Node)) return;

      if (event.key === "Escape" && errorRef.current && onCloseRef.current) {
        event.stopImmediatePropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      previouslyFocused.current?.focus?.();
    };
  }, []);

  const progress = attempt?.progress;
  const phase = (progress?.phase ?? AnalyzeProgressDtoPhaseEnum.Starting) as string;
  const total = progress?.total ?? 0;
  const done = progress?.done ?? 0;
  const current = progress?.current ?? null;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const isError = !!error;
  const isDone = attempt?.status === "SUCCESS" || phase === "done";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-busy={!isDone && !isError}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
    >
      <div
        ref={panelRef}
        className="w-[min(620px,96vw)] rounded-2xl border border-od-border bg-od-surface shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-7 pt-6 pb-5">
          <div className="flex items-start justify-between">
            <div>
              <h3
                id={titleId}
                className="text-od-text text-2xl font-semibold tracking-tight"
              >
                {isError
                  ? "The analysis stopped"
                  : isDone
                  ? "Analysis complete"
                  : datasourceName
                  ? `Analyzing ${datasourceName}`
                  : "Analyzing your database"}
              </h3>
              <p className="text-od-muted text-sm mt-1">
                {isError
                  ? "OnlyDash could not read the schema."
                  : isDone
                  ? "Analysis complete. Your tables open in a moment."
                  : `${analysePhaseLabel(progress)} · ${analyseElapsedLabel(
                      elapsedSeconds,
                    )} elapsed`}
              </p>
            </div>
            {isError && onClose && (
              <Button
                ref={closeRef}
                onClick={onClose}
                variant="ghost" size="icon" className="text-sm"
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 18 }} aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>

        {/* Progress body */}
        <div className="px-7 py-6">
          {!isError && (
            <>
              <div className="flex items-center justify-between text-xs text-od-muted mb-2">
                <span className="font-semibold">
                  {analysePhaseLabel(progress)}
                </span>
                <span
                  aria-live={!isDone && !isError ? "polite" : undefined}
                  aria-atomic={!isDone && !isError ? true : undefined}
                >
                  {total > 0 ? (
                    <>
                      <span className="text-od-text font-semibold">
                        {done}
                      </span>
                      <span> / {total} tables</span>
                    </>
                  ) : (
                    <span>Connecting…</span>
                  )}
                </span>
              </div>
              <div
                className="relative h-2.5 rounded-full bg-od-bg overflow-hidden ring-1 ring-od-border"
                role="progressbar"
                aria-valuemin={0}
                aria-valuenow={total > 0 ? done : undefined}
                aria-valuemax={total > 0 ? total : undefined}
                aria-label={analysePhaseLabel(progress)}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-od-primary rounded-full transition-[width] duration-500 ease-out"
                  style={{ width: `${Math.max(2, pct)}%` }}
                />
                {!isDone && !reduceMotion && (
                  <div
                    className="absolute inset-y-0 left-0 w-1/3 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-od-text/25 to-transparent"
                    style={{ animationDuration: "1.6s" }}
                  />
                )}
              </div>
              <div className="mt-4 min-h-[1.5rem] text-sm text-od-muted">
                {analyseElapsedLabel(elapsedSeconds)} elapsed
                {current ? ` · ${current}` : ""}
              </div>
            </>
          )}

          {isError && (
            <div className="rounded-md border border-od-danger bg-od-surface-2 p-4 text-sm text-od-danger">
              <p className="font-semibold mb-1">{error}</p>
              <p className="text-xs">
                OnlyDash saved the connection string. Change it and try
                again.
              </p>
            </div>
          )}
        </div>

        {/* Tip strip: one fixed tip for the whole open (WCAG 2.2.2). */}
        <div className="px-7 pb-5 pt-1">
          <p className="text-sm text-od-muted leading-relaxed">
            {TIPS[tip]}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalyseProgressModal;
